// Stream-domain slice: SSE subscribe/disconnect, the _handleSseEvent hub,
// worker/team activity reducers, and canvas/public-session callbacks.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import { getLiveStreamController } from "./agentStreamController";
import {
  extractMessageAddedChatMessage,
  extractTaskQueueEvent,
  getSseEventPayload,
  getSseEventType,
  isTaskQueueSseEvent,
} from "../../utils/ChatEventReducer";
import {
  reduceTeamActivity,
  markAskHumanAnswered,
} from "./conversation/stream/teamActivityReducer";
import {
  getTeamChildSession,
} from "../../api/teamsApi";
import {
  critical as _crit,
  findUnresolvedStreamParts,
  summarizeParts,
} from "../../utils/ChatStreamLogger";
import {
  normalizeSessionStatusReason,
  _ACTIVE_RUN_EVENT_TYPES,
  _isStreamableSessionStatus,
  _isKnownStreamableSessionStatus,
  _terminalClearReason,
  _hasIncompleteWorkers,
  _hasRunningTeamActivity,
  _isTeamSessionDetail,
  _isEmptyAssistantStreamMessage,
  _withAppMessageId,
} from "./agentStoreUtils";
import useChatStreamStore from "./useChatStreamStore";
import useTaskQueueStore from "./useTaskQueueStore";
import useQueuedMessageStore from "./useQueuedMessageStore";

// ── SSE event handlers ─────────────────────────────────────
// One named handler per event type, dispatched from _handleSseEvent via
// SSE_HANDLERS. Each is individually testable and receives the store accessors
// plus the parsed payload; message_added also needs the raw eventName/data to
// re-extract its chat message. The pre-dispatch side effects (submitted→stream
// promotion, tool-lifecycle recording, task-queue routing) stay in
// _handleSseEvent so they run for every event regardless of type.

function handleSnapshot(get, set, chatSessionId, payload) {
  if (!payload) {
    set({ sessionDetailLoading: false });
    return;
  }
  const currentDetail = get().sessionDetail;
  const currentChatStatus = get().chatStatus;
  const isSameSession = currentDetail?.chatSessionId === chatSessionId;
  const isTerminalSnapshot = isSameSession
    ? Boolean(_terminalClearReason(payload.status, currentChatStatus, "sseSnapshot"))
    : false;
  if (isTerminalSnapshot) {
    // A terminal snapshot mid-stream means the session finished while we were
    // briefly disconnected. Skip _replaceMessages here to avoid clobbering the
    // live-streamed messages; only record the terminal session status. chatStatus
    // is owned by _applyLifecycle (the snapshot's terminal backend status drives
    // it through the sequence-gated reducer in _handleSseEvent).
    set((s) => ({
      sessionDetailLoading: false,
      sessionDetail:
        s.sessionDetail && s.sessionDetail.chatSessionId === chatSessionId
          ? { ...s.sessionDetail, status: payload.status }
          : s.sessionDetail,
    }));
    return;
  }
  get()._replaceMessages(chatSessionId, payload.messages, payload);
  set({ sessionDetailLoading: false });
}

function handleMessageAdded(get, set, chatSessionId, _payload, { eventName, data }) {
  const msg = extractMessageAddedChatMessage(eventName, data);
  if (!msg) {
    return;
  }
  if (msg.role === "assistant") {
    let compId = null;
    const parts = msg.parts || [];
    for (const part of parts) {
      if (part.type === "tool" && part.input) {
        const inp = typeof part.input === "string" ? (() => { try { return JSON.parse(part.input); } catch (_e) { return null; } })() : part.input;
        if (inp?.componentId) { compId = inp.componentId; break; }
      }
    }
    if (!compId && msg.toolCall) {
      const toolArgs = msg.toolCall.arguments;
      if (typeof toolArgs === "string") {
        try { compId = JSON.parse(toolArgs)?.componentId; } catch (_e) { /* ignore */ }
      } else if (toolArgs && typeof toolArgs === "object") {
        compId = toolArgs.componentId;
      }
    }
    if (compId) {
      const handler = get()._canvasUpdateHandler;
      if (handler) handler({ action: '_tool_activity_start', componentId: compId });
    }
  }

  // Normalize `.id` to the app message id; see `_withAppMessageId`. Keeps
  // the live stream reducer and controller finding this turn across
  // step-start iterations instead of minting a duplicate (flicker).
  const normalized = _withAppMessageId(msg);

  const currentMsgs = get().sessionDetail?.messages || [];
  const id = normalized.messageId;

  if (msg.role === "user") {
    if (id) {
      const idExists = currentMsgs.some(
        (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
      );
      if (idExists) {
        return;
      }
    }
    const contentExists = currentMsgs.some(
      (m) => !m._optimistic && m.role === "user" && m.content === msg.content
    );
    if (contentExists) {
      return;
    }
  }

  if (_isEmptyAssistantStreamMessage(normalized)) {
    return;
  }

  const isAssistantUpdate =
    normalized.role === "assistant" &&
    id &&
    currentMsgs.some(
      (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
    );

  get()._upsertMessage(chatSessionId, normalized);

  if (isAssistantUpdate) {
    getLiveStreamController().invalidatePendingFlushForSession(chatSessionId);
  }

  if (msg.role === "assistant") {
    const parts = normalized.parts || [];
    const hasDiagnosticParts = parts.some(
      (p) => p.type === "tool" || p.type === "reasoning" || p.state === "streaming"
    );
    if (hasDiagnosticParts) {
      const unresolvedParts = findUnresolvedStreamParts([normalized]);
      _crit(
        "AgentStore",
        "info",
        `message_added assistant parts session=${chatSessionId} msgId=${normalized.messageId || normalized.id} msgStatus=${normalized.status || "?"} unresolvedParts=${unresolvedParts.length} parts={${summarizeParts(parts)}}`
      );
    }
  }
}

function handleStatusChanged(get, set, chatSessionId, payload) {
  const nextStatus = typeof payload === "string" ? payload : payload?.status;
  // chatStatus is owned by the reducer funnel; this handler only mutates sessionDetail.
  set((s) => {
    if (
      s.sessionDetail &&
      s.sessionDetail.chatSessionId === chatSessionId
    ) {
      return {
        sessionDetail: {
          ...s.sessionDetail,
          status: nextStatus,
          statusReason:
            nextStatus === "ERROR"
              ? s.sessionDetail.statusReason || null
              : null,
        },
      };
    }
    return {};
  });
  if (get()._isPublicSession && nextStatus !== "IN_PROGRESS") {
    const cb = get()._publicSessionChangeCallback;
    if (cb) cb();
  }
}

function handleSessionComplete(get, set, chatSessionId, payload) {
  // Flush any not-yet-flushed live turn so the final assistant turn is in
  // sessionDetail.messages before finalizing (a cancel can land between the
  // last chunk and its scheduled animation frame).
  getLiveStreamController().flushPendingForSession(chatSessionId);

  // On a cancel, the partial response is the final in-flight assistant
  // message that has visible answer text. Flag only that one interrupted so
  // the UI shows the "stopped" marker immediately. This mirrors the backend,
  // which persists exactly that message with interrupted=true (earlier
  // tool/reasoning iterations are persisted normally or not at all), so a
  // later reload reconciles to the identical state without spurious markers
  // on prior bubbles. The streamed turn already lives in
  // sessionDetail.messages (the single source of truth), so it is marked in
  // place — there is no separate builder copy to re-upsert.
  if ((payload?.status || "") === "CANCELLED") {
    const msgs = get().sessionDetail?.messages || [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (
        m &&
        m.role === "assistant" &&
        Array.isArray(m.parts) &&
        m.parts.some(
          (p) => p?.type === "text" && typeof p.text === "string" && p.text.trim()
        )
      ) {
        get()._upsertMessage(chatSessionId, { ...m, interrupted: true });
        break;
      }
    }
  }

  getLiveStreamController().clearCursor(chatSessionId);
  // chatStatus is owned by the reducer funnel; this handler only finalizes
  // messages + sessionDetail.
  set((s) => {
    const updates = {};
    if (
      s.sessionDetail &&
      s.sessionDetail.chatSessionId === chatSessionId
    ) {
      const completion = payload || {};
      const newStatus = completion.status || "COMPLETED";
      const newIterations =
        completion.iterationsUsed ?? s.sessionDetail.iterationsUsed;
      const completionReason = normalizeSessionStatusReason(
        completion.reason || completion.error
      );
      const currentReason = normalizeSessionStatusReason(
        s.sessionDetail.statusReason
      );
      const nextStatusReason =
        completionReason || (newStatus === "ERROR" ? currentReason : null);
      updates.sessionDetail = {
        ...s.sessionDetail,
        status: newStatus,
        iterationsUsed: newIterations,
        statusReason: nextStatusReason,
      };
    }
    return updates;
  });
  if (!get()._isPublicSession) {
    get().refreshSessionInList(chatSessionId);
    // For a TEAM session, the final visible message is projected onto the
    // parent transcript synchronously on the coordinator thread *before*
    // session_complete fires, so a reconcile here cannot race persistence
    // (unlike a single-agent turn). Force one to guarantee the final
    // projected message renders during the stream even if its message_added
    // event raced the emitter teardown — the SSE-delivery gap that left the
    // last message missing until a manual refresh.
    const sd = get().sessionDetail;
    if (
      sd && sd.chatSessionId === chatSessionId &&
      _isTeamSessionDetail(sd)
    ) {
      get().fetchSessionDetail(chatSessionId, { silent: true, force: true });
    }
    // For a single-agent turn, intentionally no fetchSessionDetail here.
    // session_complete is the authoritative end-of-stream signal —
    // sessionDetail.status, iterationsUsed, statusReason were just written
    // from the payload above, and all messages arrived via SSE. A REST fetch
    // here races backend persistence and the response (which is missing the
    // just-streamed messages) overwrites the live SSE state via _replaceMessages.
  } else {
    const cb = get()._publicSessionChangeCallback;
    if (cb) cb();
  }
}

function handleWorkerStatusChanged(get, set, _chatSessionId, payload) {
  if (payload?.workerSessionId) {
    set((s) => ({
      _workerStatuses: {
        ...s._workerStatuses,
        [payload.workerSessionId]: payload,
      },
    }));
  }
}

function handleCanvasUpdate(get, _set, _chatSessionId, payload) {
  const handler = get()._canvasUpdateHandler;
  if (handler) {
    handler(payload);
  }
}

function handleToolActivity(get, _set, _chatSessionId, payload) {
  if (payload?.componentId) {
    const handler = get()._canvasUpdateHandler;
    if (handler) handler({ action: '_tool_activity_start', componentId: payload.componentId });
  }
}

function handleQueuedMessage(get, _set, chatSessionId, payload, { type }) {
  const queuedMessage = payload?.queuedMessage || payload;
  if (type === "queued_message_dispatched") {
    const current =
      useQueuedMessageStore
        .getState()
        .bySessionId[chatSessionId]?.find(
          (item) => item.queueId === queuedMessage?.queueId
        ) || {};
    const dispatched = { ...current, ...queuedMessage };
    if (dispatched.message) {
      get()._upsertMessage(chatSessionId, {
        messageId: `queued-dispatch:${dispatched.queueId}`,
        role: "user",
        content: dispatched.message,
        imageUrls: dispatched.imageUrls || [],
        timestamp: new Date(),
        clientRequestId: dispatched.clientRequestId,
        senderUserId:
          dispatched.sourceType === "HUMAN" ? dispatched.sourceId : undefined,
        senderName: dispatched.sourceDisplayName,
        senderAgentId:
          dispatched.sourceType === "AGENT" ? dispatched.sourceId : undefined,
        senderAgentName:
          dispatched.sourceType === "AGENT"
            ? dispatched.sourceDisplayName
            : undefined,
        _optimistic: true,
      });
    }
  }
  useQueuedMessageStore
    .getState()
    .applyQueueEvent(chatSessionId, type, queuedMessage);
}

const SSE_HANDLERS = {
  snapshot: handleSnapshot,
  message_added: handleMessageAdded,
  status_changed: handleStatusChanged,
  session_complete: handleSessionComplete,
  worker_status_changed: handleWorkerStatusChanged,
  canvas_update: handleCanvasUpdate,
  tool_activity: handleToolActivity,
  queued_message_added: handleQueuedMessage,
  queued_message_updated: handleQueuedMessage,
  queued_message_cancelled: handleQueuedMessage,
  queued_message_dispatched: handleQueuedMessage,
};

// Single source of per-type lifecycle metadata for SSE events. An entry means
// the event is funneled through the sequence-gated reducer (chatLifecycleReducer.js);
// its `backendStatus(payload)` resolves the backend session status the event
// carries (the value that drives the §4.4 status-derivation table), or null for
// activity-only events whose sole lifecycle effect is advancing submitted→streaming.
// Types absent from this table AND from _ACTIVE_RUN_EVENT_TYPES (canvas_update,
// team_child_activity, task-queue) have no chatStatus impact and skip the funnel.
const SSE_LIFECYCLE_META = {
  snapshot: { backendStatus: (p) => p?.status ?? null },
  status_changed: {
    backendStatus: (p) => (typeof p === "string" ? p : p?.status) ?? null,
  },
  session_complete: { backendStatus: (p) => p?.status || "COMPLETED" },
  // message_added + the activity events (tool.*, etc.) carry no backend status;
  // their arrival alone advances submitted→streaming.
  message_added: { backendStatus: () => null },
};

// Returns the lifecycle metadata for an SSE event type, or null if the event does
// not touch chatStatus (and so skips the reducer funnel). Activity run events live
// in the shared _ACTIVE_RUN_EVENT_TYPES set and are treated as activity-bearing
// with no backend status.
function _sseLifecycleMeta(type) {
  if (SSE_LIFECYCLE_META[type]) return SSE_LIFECYCLE_META[type];
  if (_ACTIVE_RUN_EVENT_TYPES.has(type)) return { backendStatus: () => null };
  return null;
}

export function createStreamSlice(set, get) {
  return {
  _workerStatuses: {},
  /** Map<nodeId, { nodeId, agentId, agentName, childSessionId, label, status,
   *  cursor, messages, persistedMessages, startedAt, finishedAt }> — live per-node
   *  team member activity, rebuilt from the team_child_activity relay. */
  _teamActivityByRun: {},

  disconnectStream: (options) => {
    useChatStreamStore.getState().disconnectStream(options);
  },

  // Applies one team_child_activity relay event into the per-run activity slice via the pure
  // reduceTeamActivity reducer (see conversation/stream/teamActivityReducer). The reducer returns
  // the same map reference for no-op events (unknown finish, replay after completion) so we skip
  // the state update.
  _applyTeamActivity: (data) => {
    const payload = getSseEventPayload(data);
    if (!payload) return;
    set((s) => {
      const next = reduceTeamActivity(s._teamActivityByRun, payload);
      return next === s._teamActivityByRun ? {} : { _teamActivityByRun: next };
    });
  },

  // Optimistically flip a worker's inline ask_human card to ANSWERED (see markAskHumanAnswered in
  // conversation/stream/teamActivityReducer). The reducer returns the same map reference when
  // nothing matched, so we skip the state update.
  _markTeamAskHumanAnswered: (questionId, answerPatch) => {
    set((s) => {
      const next = markAskHumanAnswered(s._teamActivityByRun, questionId, answerPatch);
      return next === s._teamActivityByRun ? {} : { _teamActivityByRun: next };
    });
  },

  // Lazily fetch a team run's full persisted child transcript (for cold load /
  // refresh, when the live relay produced no streamed messages). Fetched once per
  // run; the card prefers persistedMessages over the live stream. `seed`
  // reconstructs a missing entry from the projected message metadata so persisted
  // cards (no live activity) can still hydrate on expand.
  _loadTeamChildTranscript: async (runId, childSessionId, seed = null) => {
    const entry = get()._teamActivityByRun[runId];
    if (entry && (entry._loadingTranscript || entry.persistedMessages)) return;
    const parentSessionId = get().sessionDetail?.chatSessionId;
    if (!parentSessionId || !childSessionId) return;

    const mark = (patch) =>
      set((s) => {
        const e = s._teamActivityByRun[runId] || seed || { runId };
        return {
          _teamActivityByRun: { ...s._teamActivityByRun, [runId]: { ...e, ...patch } },
        };
      });

    mark({ _loadingTranscript: true });
    const res = await getTeamChildSession(parentSessionId, childSessionId);
    const messages = Array.isArray(res?.data?.messages) ? res.data.messages : [];
    mark({ persistedMessages: messages, _loadingTranscript: false });
  },

  /**
   * Subscribe to session SSE events.
   * For private sessions: subscribeToSession(chatSessionId)
   * For public sessions:  subscribeToSession(chatSessionId, { publicSessionToken: "pct-..." })
   */
  subscribeToSession: async (chatSessionId, options = {}) => {
    const isPublic = !!options?.publicSessionToken;
    const connectId = isPublic ? options.publicSessionToken : chatSessionId;
    set({ _isPublicSession: isPublic });
    // Start each (re)subscribe with a fresh stream cursor so replayed chunks
    // rebuild the in-flight turn from scratch rather than continuing stale
    // pointers from a previous connection. Clearing all cursors (not just this
    // session's) also reclaims any left over from a session the user navigated
    // away from mid-stream, since only one session streams at a time.
    getLiveStreamController().clearAllCursors();

    const result = await useChatStreamStore.getState().subscribeToSession(
      connectId,
      chatSessionId,
      {
        reconnectWhileSubscribed: options?.reconnectWhileSubscribed === true,
        forceReconnect: options?.forceReconnect === true,
        skipAuth: isPublic,
        onEvent: (eventName, data, sessionId, seq) => {
          const eventType = getSseEventType(eventName, data);
          if (eventType === "team_child_activity") {
            get()._applyTeamActivity(data);
            return;
          }
          if (eventType === "ui_chunk") {
            get()._applyStreamChunk(eventName, data, sessionId, seq);
            return;
          }
          get()._handleSseEvent(eventName, data, sessionId, seq);
        },
        shouldReconnect: () => {
          const status = get().sessionDetail?.status;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          return _isStreamableSessionStatus(status) || hasWorkers || hasTeam;
        },
        onPollFallback: (sid) => {
          get().fetchSessionDetail(sid, { silent: true });
        },
        pollIntervalMs: 3000,
        isSessionActive: () => {
          const s = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          return (
            _isStreamableSessionStatus(s?.status) || cs !== 'ready' || hasWorkers || hasTeam
          );
        },
        isSessionTerminal: () => {
          const d = get().sessionDetail;
          const cs = get().chatStatus;
          const hasWorkers = _hasIncompleteWorkers(get()._workerStatuses);
          const hasTeam = _hasRunningTeamActivity(get()._teamActivityByRun);
          if (!d || d.chatSessionId !== chatSessionId) return false;
          if (_isStreamableSessionStatus(d.status)) return false;
          if (cs !== 'ready') return false;
          return !hasWorkers && !hasTeam;
        },
      }
    );
    if (result?.aborted) {
      return;
    }
    // The live stream ended. Drop sseAuthority locally before reconciling so the
    // gated REST fetch below is NOT rejected as restSupersededByLiveSse (§4.3).
    // The global stream-state→connState bridge (useAgentStore.js) also emits this
    // idle transition, but we apply it here too rather than depend on that
    // subscriber having fired first — the connState reducer is idempotent
    // (connStateNoChange when authority already matches), so the redundant apply
    // is free and removes the cross-module ordering assumption. Then reconcile
    // chatStatus from the backend through the gated REST path rather than
    // force-writing it here, only when the turn is still busy or the connection
    // failed; a clean close after a terminal already settled chatStatus.
    get()._applyLifecycle({ kind: 'connState', sessionId: chatSessionId, state: 'idle' });
    const currentStatus = get().chatStatus;
    if (currentStatus === 'streaming' || currentStatus === 'error' || result?.connectionFailed) {
      get().fetchSessionDetail(chatSessionId, { silent: true });
    }
  },

  /**
   * Reopen the chat stream for an already-open session that was reactivated
   * out-of-band. When an artifact requested mid-conversation is submitted on a
   * separate page, the backend flips the (COMPLETED) session back to a
   * streamable status. That session's chat stream closed when the prior turn
   * completed, and the subscription effect is keyed only on selectedSessionId,
   * so nothing re-subscribes it — forms keep the session streamable so their
   * stream never closes, which is why this gap is artifact-specific. The
   * sessions-list status is the surface-independent signal that the session is
   * live again.
   */
  reopenReactivatedSessionStream: (chatSessionId, status) => {
    if (!chatSessionId || chatSessionId === "__pending__") return;
    if (!_isKnownStreamableSessionStatus(status)) return;
    // Never tear down a healthy live turn: while a stream is open the stream
    // store stays subscribed to this session; it resets to null on clean close.
    if (
      useChatStreamStore.getState().getSubscribedSessionId() === chatSessionId
    ) {
      return;
    }
    get().subscribeToSession(chatSessionId, {
      reconnectWhileSubscribed: true,
      forceReconnect: true,
    });
  },

  _handleSseEvent: (eventName, data, chatSessionId, seq = null) => {
    const type = getSseEventType(eventName, data);
    const payload = getSseEventPayload(data);
    const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId[chatSessionId];
    _crit(
      "AgentStore",
      "info",
      `_handleSseEvent type=${type} session=${chatSessionId} sseState=${streamState || 'idle'} chatStatus=${get().chatStatus}`
    );
    // Pre-dispatch side effects that apply across event types.
    if (isTaskQueueSseEvent(type)) {
      const queueEvent = extractTaskQueueEvent(eventName, data);
      if (queueEvent && chatSessionId) {
        useTaskQueueStore.getState().setTaskQueueEvent(chatSessionId, queueEvent);
      }
    }

    // Single chatStatus funnel: every status-bearing or activity-bearing SSE
    // event is sequence-gated and reduced into chatStatus here, so the per-type
    // handlers below never write chatStatus. seq is the backend-minted SSE `id:`
    // (threaded in step 1); the reducer rejects stale/duplicate seqs from replay.
    const lifecycleMeta = _sseLifecycleMeta(type);
    if (lifecycleMeta) {
      get()._applyLifecycle({
        kind: "sse",
        sessionId: chatSessionId,
        eventType: type,
        seq,
        backendStatus: lifecycleMeta.backendStatus(payload),
      });
    }

    const handler = SSE_HANDLERS[type];
    if (handler) {
      handler(get, set, chatSessionId, payload, { eventName, data, type });
    }
  },

  _canvasUpdateHandler: null,
  setCanvasUpdateHandler: (fn) => {
    set({ _canvasUpdateHandler: fn });
  },

  _publicSessionChangeCallback: null,
  setPublicSessionChangeCallback: (fn) => set({ _publicSessionChangeCallback: fn }),
  };
}
